import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const VideoShowcaseSection = () => {
  const sectionRef = useRef(null);
  const videoContainerRef = useRef(null);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    const container = videoContainerRef.current;
    const section = sectionRef.current;
    const iframe = iframeRef.current;

    if (!container || !section) return;

    // Load Vimeo player script and initialize player
    const loadVimeoScript = () => {
      return new Promise((resolve) => {
        const existingScript = document.querySelector('script[src="https://player.vimeo.com/api/player.js"]');
        
        if (existingScript && window.Vimeo) {
          // Script already loaded
          if (iframe) {
            try {
              playerRef.current = new window.Vimeo.Player(iframe);
              playerRef.current.ready().then(() => resolve()).catch(() => resolve());
            } catch (e) {
              resolve();
            }
          } else {
            resolve();
          }
          return;
        }

        if (window.Vimeo && iframe) {
          // Vimeo already available, just create player
          try {
            playerRef.current = new window.Vimeo.Player(iframe);
            playerRef.current.ready().then(() => resolve()).catch(() => resolve());
          } catch (e) {
            resolve();
          }
          return;
        }

        // Load the script
        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.async = true;
        script.onload = () => {
          if (iframe && window.Vimeo) {
            try {
              playerRef.current = new window.Vimeo.Player(iframe);
              playerRef.current.ready().then(() => resolve()).catch(() => resolve());
            } catch (e) {
              resolve();
            }
          } else {
            resolve();
          }
        };
        script.onerror = () => resolve();
        document.body.appendChild(script);
      });
    };

    let cleanup = null;
    let intersectionObserver = null;

    // Wait for player to be ready before setting up ScrollTriggers
    loadVimeoScript().then(() => {
      // Use GSAP context for automatic cleanup
      const ctx = gsap.context(() => {
        // Initial state - video starts small and scaled down
        gsap.set(container, {
          scale: 0.3,
          opacity: 0,
          borderRadius: '24px',
        });

        // Create scroll animation - only affects the video container
        ScrollTrigger.create({
          trigger: section,
          start: 'top 80%',
          end: 'top 20%',
          scrub: 1,
          onUpdate: (self) => {
            const progress = self.progress;
            
            // Scale from 0.3 to 1 as user scrolls
            const scale = gsap.utils.interpolate(0.3, 1, progress);
            const opacity = gsap.utils.interpolate(0, 1, progress);
            const borderRadius = gsap.utils.interpolate(24, 0, progress);
            
            if (container && container.parentNode) {
              gsap.set(container, {
                scale: scale,
                opacity: opacity,
                borderRadius: `${borderRadius}px`,
              });
            }
          },
        });

        // Auto-play video when section enters viewport
        ScrollTrigger.create({
          trigger: section,
          start: 'top 80%',
          end: 'bottom 20%',
          onEnter: () => {
            if (playerRef.current) {
              playerRef.current.setVolume(1).then(() => {
                return playerRef.current.play();
              }).catch((error) => {
                // Autoplay may be blocked by browser, this is expected
                console.log('Autoplay prevented:', error);
              });
            }
          },
          onLeave: () => {
            // Scrolled down past the section
            if (playerRef.current) {
              playerRef.current.pause().catch(() => {});
            }
          },
          onEnterBack: () => {
            // Scrolled back up into the section
            if (playerRef.current) {
              playerRef.current.setVolume(1).then(() => {
                return playerRef.current.play();
              }).catch((error) => {
                console.log('Autoplay prevented:', error);
              });
            }
          },
          onLeaveBack: () => {
            // Scrolled up past the section
            if (playerRef.current) {
              playerRef.current.pause().catch(() => {});
            }
          },
        });
      }, section); // Context scoped to section

      // Use Intersection Observer as a backup to ensure video pauses when section is out of view
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && playerRef.current) {
              // Section is not visible, pause the video
              playerRef.current.pause().catch(() => {});
            }
          });
        },
        {
          threshold: 0.1, // Trigger when less than 10% of section is visible
          rootMargin: '0px',
        }
      );

      intersectionObserver.observe(section);

      cleanup = () => {
        // GSAP context automatically cleans up ScrollTriggers
        ctx.revert();
        // Clean up Intersection Observer
        if (intersectionObserver) {
          intersectionObserver.disconnect();
        }
      };
    });

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="video-showcase-section" id="video-showcase">
      <div className="video-section-header">
        <span className="section-label">How It Works</span>
        <h2 className="section-title">
          See Our System
          <span className="gradient-text"> In Action</span>
        </h2>
      </div>

      <div ref={videoContainerRef} className="video-container">
        <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
          <iframe
            ref={iframeRef}
            src="https://player.vimeo.com/video/1137967702?badge=0&byline=0&title=0&portrait=0&autopause=0&autoplay=1&player_id=0&app_id=58479"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture; "
            referrerPolicy="strict-origin-when-cross-origin"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
            title="Agricverse"
            className="vimeo-iframe"
          />
        </div>
      </div>
    </section>
  );
};

export default VideoShowcaseSection;

